import Logger from "./core/internal/logger"
import HackleClientImpl, { BrowserHackleClient as HackleClient, DevTools, PageView } from "./hackle/index.browser"
import { isHackleWebApp } from "./hackle/webapp/HackleWebAppClient"
import {
  BROWSER_BATCH_SIZE,
  BROWSER_FLUSH_INTERVAL,
  COOKIE_EXPIRE_DAYS,
  DEFAULT_ENGAGEMENT_DURATION_MILLIS,
  FETCH_THROTTLE_INTERVAL_MILLIS,
  IAM_HIDE_STORAGE_PREFIX,
  IAM_IMPRESSION_STORAGE_PREFIX,
  CAMPAIGN_DELAY_STORAGE_KEY,
  LOCAL_STORAGE_KEY_PREFIX,
  METRIC_FLUSH_INTERVAL,
  OVERRIDE_AB_STORAGE_PREFIX,
  OVERRIDE_FF_STORAGE_PREFIX,
  SPLIT_URL_REDIRECT_CHECK_COOKIE_EXPIRE_DAYS,
  SPLIT_URL_REDIRECT_CHECK_COOKIE_KEY
} from "./config"
import { getUserId, IdentifierManager, removeUserId, setUserId } from "./hackle/user/index.browser"
import "core-js/features/promise"
import "core-js/features/array"
import BrowserEventProcessor from "./hackle/event/processor/index.browser"
import HackleCore from "./core/HackleCore"
import { GlobalErrorHandler } from "./hackle/trace/GlobalErrorHandler"
import { EventRepositoryImpl } from "./hackle/event/repository/index.browser"
import { UserManagerImpl, UserStorage } from "./hackle/user/manager/index.browser"
import { SessionManagerImpl } from "./hackle/session/manager/index.browser"
import { CookieStorage } from "./hackle/storage/CookieStorage.browser"
import { SessionEventTracker } from "./hackle/session/track/index.browser"
import { KeyTransformStorage, ListStorage, WindowLocalStorage } from "./core/internal/storage/Storage"
import { MonitoringMetricRegistry } from "./core/internal/metrics/monitoring/MonitoringMetricRegistry"
import { Metrics } from "./core/internal/metrics/Metrics"
import { HackleUserManualOverrideStorage } from "./core/internal/evaluation/target/ManualOverrideStorage"
import { HackleUserExplorerBase, HackleUserExplorerImpl } from "./core/internal/user/UserExplorer"
import { Urls } from "./hackle/http/Urls"
import { InAppMessageHiddenLocalStorage } from "./hackle/iam/storage/InAppMessageHiddenLocalStorage"
import { HackleCoreContext } from "./core/internal/HackleCoreContext"
import TargetMatcher from "./core/internal/evaluation/match/TargetMatcher"
import {
  InAppMessageActionHandlerFactory,
  InAppMessageCloseActionHandler,
  InAppMessageHiddenActionHandler,
  InAppMessageLinkActionHandler,
  InAppMessageLinkAndCloseActionHandler,
  InAppMessageLinkNewTabActionHandler,
  InAppMessageLinkNewTabAndCloseActionHandler,
  InAppMessageLinkNewWindowActionHandler,
  InAppMessageLinkNewWindowAndCloseActionHandler
} from "./hackle/iam/ui/event/InAppMessageActionHandler"
import { InAppMessageEventTracker } from "./hackle/iam/ui/event/InAppMessageEventTracker"
import { InAppMessageUi } from "./hackle/iam/ui/InAppMessageUi"
import { SystemClock } from "./core/internal/util/TimeUtil"
import { ScriptInAppMessageViewFactory } from "./hackle/iam/ui/view/factory/ScriptInAppMessageViewFactory"
import { InAppMessageRenderScriptLoader } from "./hackle/iam/ui/script/InAppMessageRenderScriptLoader"
import { User } from "./core/internal/model/model"
import { HackleSessionPolicy } from "./public/model"
import { InAppMessageEventTriggerRuleMatcher } from "./hackle/iam/trigger/InAppMessageEventTriggerMatcher"
import { InAppMessageImpressionLocalStorage } from "./hackle/iam/storage/InAppMessageImpressionStorage"
import {
  InAppMessageViewEventActionHandler,
  InAppMessageViewEventHandlerFactory,
  InAppMessageViewEventTrackHandler
} from "./hackle/iam/ui/event/InAppMessageViewEventHandler"
import { InAppMessageViewEventHandleProcessor } from "./hackle/iam/ui/event/InAppMessageViewEventHandleProcessor"
import { InAppMessageEventMatcher } from "./hackle/iam/trigger/InAppMessageEventMatcher"
import {
  InAppMessageActionInteractionHandler,
  InAppMessageCloseInteractionHandler,
  InAppMessageImageImpressionInteractionHandler,
  InAppMessageInteractionHandlerFactory
} from "./hackle/iam/ui/event/InAppMessageInteractionHandler"
import {
  UrlLinkHandler,
  UrlLinkNewTabHandler,
  UrlLinkNewWindowHandler,
  UrlReplaceHandler
} from "./hackle/iam/ui/internal/UrlHandler"
import { CampaignManager } from "./hackle/attribution/CampaignManager"
import { CampaignStorage } from "./hackle/attribution/CampaignStorage"
import { CampaignParser } from "./hackle/attribution/CampaignParser"
import GoogleAnalyticsManager from "./hackle/external/GoogleAnalyticsManager"
import GoogleAnalyticsDeterminer from "./hackle/external/GoogleAnalyticsDeterminer"
import { Configurations, InternalConfig } from "./hackle/configuration/index.browser"
import { CompositeSynchronizer } from "./core/internal/sync/CompositeSynchronizer"
import { PollingSynchronizer } from "./core/internal/sync/PollingSynchronizer"
import { TimerScheduler } from "./core/internal/scheduler/TimerScheduler"
import { HttpWorkspaceFetcher } from "./core/internal/workspace/HttpWorkspaceFetcher"
import { DefaultUserCohortFetcher } from "./core/internal/user/UserCohortFetcher"
import { DefaultUserTargetFetcher } from "./core/internal/user/UserTargetFetcher"
import { WorkspaceManager } from "./core/internal/workspace/WorkspaceManager"
import { BrowserEventDispatcher } from "./hackle/event/dispatcher/index.browser"
import { EventTransport } from "./core/internal/event/EventTransport"
import { BrowserEventRetryManager } from "./hackle/event/retry/index.browser"
import { DefaultEventProcessor } from "./core/internal/event/EventProcessor"
import { Transport } from "./core/internal/transport/Transport"
import { Transports } from "./core/internal/transport/Transports"
import { XhrTransport } from "./core/internal/transport/XhrTransport"
import { BeaconTransport } from "./core/internal/transport/BeaconTransport"
import { SplitUrlManager } from "./hackle/split/SplitUrlManager"
import { SplitUrlDeterminer } from "./hackle/split/SplitUrlDeterminer"
import { SplitUrlMatcher } from "./hackle/split/SplitUrlMatcher"
import { SplitUrlRedirector } from "./hackle/split/SplitUrlRedirector"
import { SplitUrlResolver } from "./hackle/split/SplitUrlResolver"
import { SplitUrlRedirectedCookieStorage } from "./hackle/split/SplitUrlRedirectedCookieStorage"
import BrowserInitializeDoneEventDispatcher from "./core/internal/event/BrowserInitializeDoneEventDispatcher"
import { BrowserWorkspaceConfigStorage } from "./hackle/workspace/BrowserWorkspaceConfigStorage"
import { LifecycleManager } from "./core/internal/lifecycle/LifecycleManager"
import { Throttler } from "./core/internal/throttler/Throttler"
import { DelegatingUserEventDedupDeterminer } from "./hackle/event/dedup/DelegatingUserEventDedupDeterminer"
import { RemoteConfigEventDedupDeterminer } from "./hackle/event/dedup/RemoteConfigEventDedupDeterminer"
import { ExposureEventDedupDeterminer } from "./hackle/event/dedup/ExposureEventDedupDeterminer"
import { PageEventTracker } from "./core/internal/page/PageEventTracker"
import { PageManager } from "./core/internal/page/PageManager"
import { EngagementManager } from "./core/internal/engagement/EngagementManager"
import { EngagementEventTracker } from "./core/internal/engagement/EngagementEventTracker"
import { PIIEventManager } from "./core/internal/pii/PIIEventManager"
import DefaultBrowserPropertyProvider, { BrowserPropertyProvider } from "./hackle/property/BrowserPropertyProvider"
import InAppMessageManager from "./hackle/iam/InAppMessageManager"
import { InAppMessageExperimentEvaluator } from "./core/internal/evaluation/evalautor/iam/layout/InAppMessageExperimentEvaluator"
import { InAppMessageLayoutEvaluator } from "./core/internal/evaluation/evalautor/iam/layout/InAppMessageLayoutEvaluator"
import DelegatingEvaluator from "./core/internal/evaluation/evalautor/DelegatingEvaluator"
import { InAppMessageLayoutSelector } from "./core/internal/evaluation/evalautor/iam/layout/InAppMessageLayoutSelector"
import { InAppMessageRecorder } from "./hackle/iam/present/record/InAppMessageRecorder"
import { InAppMessagePresentProcessor } from "./hackle/iam/present/InAppMessagePresentProcessor"
import { InAppMessageIdentifierChecker } from "./hackle/iam/evaluation/InAppMessageIdentifierChecker"
import { InAppMessageDelayScheduler } from "./hackle/iam/delay/InAppMessageDelayScheduler"
import { InAppMessageDelayManager } from "./hackle/iam/delay/InAppMessageDelayManager"
import { InAppMessageDeliverProcessor } from "./hackle/iam/deliver/InAppMessageDeliverProcessor"
import { InAppMessageSchedulerFactory } from "./hackle/iam/schedule/scheduler/InAppMessageSchedulerFactory"
import { DelayedInAppMessageScheduler } from "./hackle/iam/schedule/scheduler/DelayedInAppMessageScheduler"
import { TriggeredInAppMessageScheduler } from "./hackle/iam/schedule/scheduler/TriggeredInAppMessageScheduler"
import { InAppMessageScheduleProcessor } from "./hackle/iam/schedule/InAppMessageScheduleProcessor"
import { InAppMessageScheduleActionDeterminer } from "./hackle/iam/schedule/action/InAppMessageScheduleActionDeterminer"
import { InAppMessageTriggerDeterminer } from "./hackle/iam/trigger/InAppMessageTriggerDeterminer"
import { InAppMessageTriggerHandler } from "./hackle/iam/trigger/InAppMessageTriggerHandler"
import { InAppMessageTriggerProcessor } from "./hackle/iam/trigger/InAppMessageTriggerProcessor"
import { InAppMessageResetProcessor } from "./hackle/iam/reset/InAppMessageResetProcessor"
import { InAppMessageDelayLocalStorage } from "./hackle/iam/storage/InAppMessageDelayStorage"
import UserEventFactory from "./core/internal/event/UserEventFactory"
import { EvaluationEventRecorder } from "./core/internal/evaluation/evalautor/EvaluationEventRecorder"
import InAppMessageEligibilityFlowFactory from "./core/internal/evaluation/evalautor/iam/eligibility/InAppMessageEligibilityFlowFactory"
import { InAppMessageEvaluateProcessor } from "./hackle/iam/evaluation/InAppMessageEvaluateProcessor"
import { InAppMessageLayoutResolver } from "./hackle/iam/evaluation/InAppMessageLayoutResolver"
import { StoredInAppMessageScheduler } from "./hackle/iam/schedule/scheduler/StoredInAppMessageScheduler"
import SessionUserDecorator from "./core/internal/session/SessionUserDecorator"
import SessionUserEventDecorator from "./core/internal/session/SessionUserEventDecorator"
import { LifecycleCompositeManager } from "./core/internal/lifecycle/LifecycleComposite"
import { OptOutManager } from "./core/internal/optout/OptOutManager"
import { InAppMessageViewFactories } from "./hackle/iam/ui/view/factory/InAppMessageViewFactories"
import { HtmlInAppMessageViewFactory } from "./hackle/iam/ui/view/factory/HtmlInAppMessageViewFactory"
import { InAppMessageHtmlContentResolver } from "./hackle/iam/ui/html/InAppMessageHtmlContentResolver"
import { InAppMessageHtmlContentFetcher } from "./hackle/iam/ui/html/InAppMessageHtmlContentFetcher"
import { HackleClientHolder } from "./core/HackleClientHolder"
import { createWebAppClient } from "./hackle/webapp/bootstrap"
import { installAppWebViewInAppMessageBridge } from "./hackle/webapp/iam/installAppViewInAppMessageBridge"
import {
  InAppMessageActionEventActor,
  InAppMessageCloseEventActor,
  InAppMessageImageImpressionEventActor,
  InAppMessageImpressionEventActor,
  InAppMessageViewEventActorFactory
} from "./hackle/iam/ui/event/InAppMessageViewEventActor"
import { InAppMessageBridgeFactoryFactory } from "./hackle/iam/ui/html/InAppMessageBridgeFactory"

const log = Logger.log
const hackleClientHolder = new HackleClientHolder<HackleClient>()

interface Config {
  user?: User

  debug?: boolean
  /** @deprecated use automaticPageTracking instead  */
  auto_track_page_view?: boolean
  automaticPageTracking?: boolean
  automaticRouteTracking?: boolean
  pollingIntervalMillis?: number
  exposureEventDedupIntervalMillis?: number
  /** @deprecated Use sessionPolicy.timeoutMillis instead */
  sessionTimeoutMillis?: number
  /** 세션 정책 설정 */
  sessionPolicy?: HackleSessionPolicy
  devTool?: DevTools["manager"]
  autoOpenDevTool?: boolean
  sameSiteCookie?: string
  secureCookie?: boolean
  optOutTracking?: boolean

  [key: string]: any
}

/**
 * 실행되는 런타임의 window 객체에 {@link AppWebViewInAppMessageBridge}를 주입합니다.
 *
 * Hackle App SDK에서 이 함수를 실행하여 HTML In-App Message를 사용할 수 있도록 합니다.
 */
function setAppWebViewInAppMessageBridge(): void {
  installAppWebViewInAppMessageBridge({
    targetWindow: window,
    createClientBySdkKey: (sdkKey) => createInstance(sdkKey)
  })
}

function createInstance(sdkKey: string, _config?: Config): HackleClient {
  // Config
  const config = Configurations.config(_config)

  // Logging
  Configurations.logging(config)

  // BrowserPropertyProvider
  const browserPropertyProvider = new DefaultBrowserPropertyProvider()
  const browserName = browserPropertyProvider.getBrowserProperties()["browserName"]

  // LifecycleCompositeManager
  const pageManager = new PageManager()
  const lifecycleManager = new LifecycleManager()
  const engagementManager = new EngagementManager(pageManager, DEFAULT_ENGAGEMENT_DURATION_MILLIS)
  const lifecycleComposite = new LifecycleCompositeManager(
    lifecycleManager,
    pageManager,
    engagementManager,
    typeof browserName === "string" ? browserName : null
  )

  // check WebApp client
  try {
    if (isHackleWebApp(window) && sdkKey === window._hackleApp.getAppSdkKey()) {
      return createWebAppClient({
        sdkKey,
        browserConfig: config,
        hackleApp: window._hackleApp,
        lifecycleComposite,
        browserPropertyProvider,
        browserClientFactory: createBrowserClient
      })
    }
  } catch (e) {
    log.error(`Unexpected exception while check WebApp client: ${e}`)
  }

  return createBrowserClient(sdkKey, config, lifecycleComposite)
}

function createBrowserClient(
  sdkKey: string,
  config: InternalConfig,
  lifecycleCompositeManager: LifecycleCompositeManager
): HackleClient {
  // SDK
  const sdk = Configurations.sdk(sdkKey, config)

  // check cache
  const clientCache = hackleClientHolder.getOrNull()
  if (clientCache) {
    log.debug("use already exists hackleClient")
    return clientCache
  }

  // _hackle_id
  const identifierManager = new IdentifierManager()
  identifierManager.initialize()

  // Storage
  const localStorage = new WindowLocalStorage()
  const cookieStorage = CookieStorage.getInstance({
    days: COOKIE_EXPIRE_DAYS,
    isCrossSubdomain: true,
    sameSite: config.sameSiteCookie,
    isSecure: config.secureCookie
  })

  // Beacon
  let isBeaconSupported = Configurations.isBeaconSupported()

  // Transport
  const defaultTransport = Transports.intercept(sdk, XhrTransport.create())
  const beaconTransport = Transports.intercept(sdk, BeaconTransport.create())
  let synchronousTransport: Transport = defaultTransport
  if (isBeaconSupported) {
    synchronousTransport = beaconTransport
  }

  // Synchronizer
  const compositeSynchronizer = new CompositeSynchronizer()
  const pollingSynchronizer = new PollingSynchronizer(
    compositeSynchronizer,
    new TimerScheduler("setTimeout"),
    Configurations.pollingIntervalMillis(config)
  )

  // WorkspaceManager
  const workspaceConfigStorage = new BrowserWorkspaceConfigStorage(
    sdk,
    KeyTransformStorage.postfix(localStorage, `_${sdkKey.slice(0, 8)}`)
  )
  const httpWorkspaceFetcher = new HttpWorkspaceFetcher(Urls.clientFetch(config.sdkUrl, sdk.key), defaultTransport)
  const workspaceManager = new WorkspaceManager(workspaceConfigStorage, httpWorkspaceFetcher)
  compositeSynchronizer.add(workspaceManager)

  // UserManager
  const userTargetFetcher = new DefaultUserTargetFetcher(Urls.userTargets(config.sdkUrl), defaultTransport)
  const cohortFetcher = new DefaultUserCohortFetcher(Urls.userCohorts(config.sdkUrl), defaultTransport)
  const campaignParser = new CampaignParser(lifecycleCompositeManager.pageManager)
  const userStorage = new UserStorage(KeyTransformStorage.postfix(cookieStorage, `_${sdkKey}`))
  const previousUser = userStorage.getUser()
  const initUser = config.user || null
  const userManager = new UserManagerImpl(
    getUserId(),
    userStorage,
    userTargetFetcher,
    cohortFetcher,
    campaignParser,
    lifecycleCompositeManager.pageManager,
    SystemClock.instance,
    previousUser,
    initUser
  )
  compositeSynchronizer.add(userManager)

  // SessionManager
  const sessionManager = new SessionManagerImpl(
    config.sessionPolicy,
    KeyTransformStorage.postfix(cookieStorage, `_${sdkKey.slice(8)}`)
  )
  userManager.addListener(sessionManager)

  // EventProcessor
  const repository = new EventRepositoryImpl(localStorage, `${LOCAL_STORAGE_KEY_PREFIX}_${sdkKey}`, BROWSER_BATCH_SIZE)
  const eventTransport = new EventTransport(Urls.clientDispatch(config.eventUrl), defaultTransport)
  let synchronousEventTransport: EventTransport = eventTransport
  if (isBeaconSupported) {
    synchronousEventTransport = new EventTransport(Urls.beaconDispatch(config.eventUrl, sdk.key), beaconTransport)
  }
  const retryManager = new BrowserEventRetryManager(repository, eventTransport)
  const eventDispatcher = new BrowserEventDispatcher(eventTransport, synchronousEventTransport, retryManager)
  const eventDedupDeterminer = new DelegatingUserEventDedupDeterminer([
    new RemoteConfigEventDedupDeterminer(Configurations.dedupIntervalMillis(config)),
    new ExposureEventDedupDeterminer(Configurations.dedupIntervalMillis(config))
  ])
  // OptOutManager
  const optOutManager = new OptOutManager(config.optOutTracking)

  const defaultEventProcessor = new DefaultEventProcessor(
    eventDispatcher,
    BROWSER_BATCH_SIZE,
    new TimerScheduler("setTimeout"),
    BROWSER_FLUSH_INTERVAL,
    optOutManager
  )

  const browserEventProcessor = new BrowserEventProcessor(
    defaultEventProcessor,
    eventDedupDeterminer,
    sessionManager,
    userManager
  )
  optOutManager.addListener(browserEventProcessor)

  const sessionUserDecorator = new SessionUserDecorator(sessionManager)
  const sessionUserEventDecorator = new SessionUserEventDecorator(sessionUserDecorator)
  browserEventProcessor.addDecorator(sessionUserEventDecorator)

  // InitializeEventDispatcher
  const initializeEventDispatcher = new BrowserInitializeDoneEventDispatcher()

  // Core
  const abOverrideStorage = new HackleUserManualOverrideStorage(
    new ListStorage(cookieStorage, `${OVERRIDE_AB_STORAGE_PREFIX}_${sdkKey.slice(0, 8)}`)
  )
  const ffOverrideStorage = new HackleUserManualOverrideStorage(
    new ListStorage(cookieStorage, `${OVERRIDE_FF_STORAGE_PREFIX}_${sdkKey.slice(0, 8)}`)
  )
  const inAppMessageHiddenStorage = new InAppMessageHiddenLocalStorage(
    KeyTransformStorage.prefix(localStorage, `${IAM_HIDE_STORAGE_PREFIX}_${sdkKey.slice(0, 8)}_`)
  )
  const inAppMessageImpressionStorage = new InAppMessageImpressionLocalStorage(
    KeyTransformStorage.prefix(localStorage, `${IAM_IMPRESSION_STORAGE_PREFIX}_${sdkKey.slice(0, 8)}_`)
  )

  const eventFactory = new UserEventFactory(SystemClock.instance)
  const eventRecorder = new EvaluationEventRecorder(eventFactory, browserEventProcessor)

  const core = HackleCore.create(workspaceManager, eventFactory, browserEventProcessor, [
    abOverrideStorage,
    ffOverrideStorage
  ])

  // PIIEventManager
  const piiEventManager = new PIIEventManager(core, userManager)

  // CampaignManager, SessionTracker
  const campaignManager = new CampaignManager(
    new CampaignStorage(KeyTransformStorage.postfix(cookieStorage, `_${sdkKey.slice(0, 8)}`)),
    campaignParser,
    core,
    userManager
  )
  const sessionEventTracker = new SessionEventTracker(userManager, core)

  // Add SessionListener
  // CampaignManager MUST be added before SessionTracker to include campaign properties in session events.
  sessionManager.addListener(campaignManager)
  sessionManager.addListener(sessionEventTracker)

  // InAppMessage
  const inAppMessageEventTracker = new InAppMessageEventTracker(core)
  const inAppMessageActionHandlerFactory = new InAppMessageActionHandlerFactory([
    new InAppMessageCloseActionHandler(),
    new InAppMessageLinkActionHandler(new UrlLinkHandler()),
    new InAppMessageLinkAndCloseActionHandler(new UrlLinkHandler()),
    new InAppMessageHiddenActionHandler(inAppMessageHiddenStorage, SystemClock.instance),
    new InAppMessageLinkNewWindowActionHandler(new UrlLinkNewWindowHandler()),
    new InAppMessageLinkNewWindowAndCloseActionHandler(new UrlLinkNewWindowHandler()),
    new InAppMessageLinkNewTabActionHandler(new UrlLinkNewTabHandler()),
    new InAppMessageLinkNewTabAndCloseActionHandler(new UrlLinkNewTabHandler())
  ])

  const inAppMessageViewEventActorFactory = new InAppMessageViewEventActorFactory([
    new InAppMessageImpressionEventActor(),
    new InAppMessageActionEventActor(inAppMessageActionHandlerFactory),
    new InAppMessageCloseEventActor(),
    new InAppMessageImageImpressionEventActor()
  ])

  const inAppMessageViewEventHandlerFactory = new InAppMessageViewEventHandlerFactory([
    new InAppMessageViewEventTrackHandler(inAppMessageEventTracker),
    new InAppMessageViewEventActionHandler(inAppMessageViewEventActorFactory)
  ])

  const inAppMessageViewEventHandleProcessor = new InAppMessageViewEventHandleProcessor(
    inAppMessageViewEventHandlerFactory
  )
  const inAppMessageInteractionHandlerFactory = new InAppMessageInteractionHandlerFactory([
    new InAppMessageCloseInteractionHandler(),
    new InAppMessageActionInteractionHandler(inAppMessageViewEventHandleProcessor),
    new InAppMessageImageImpressionInteractionHandler(inAppMessageViewEventHandleProcessor)
  ])
  const inAppMessageRenderScriptLoader = new InAppMessageRenderScriptLoader(
    Urls.inAppMessageRenderer(config.cdnUrl, config.IAM_RENDERER_VERSION_HEADER)
  )

  const inAppMessageBridgeFactory = InAppMessageBridgeFactoryFactory.createWebIframe(() => hackleClientHolder.get())
  const scriptInAppMessageViewFactory = ScriptInAppMessageViewFactory.create(inAppMessageRenderScriptLoader)
  const htmlInAppMessageViewFactory = HtmlInAppMessageViewFactory.create(
    new InAppMessageHtmlContentResolver(new InAppMessageHtmlContentFetcher(defaultTransport)),
    inAppMessageBridgeFactory
  )

  const inAppMessageViewFactories = new InAppMessageViewFactories([
    scriptInAppMessageViewFactory,
    htmlInAppMessageViewFactory
  ])

  const inAppMessageUi = new InAppMessageUi(
    inAppMessageViewFactories,
    inAppMessageViewEventHandleProcessor,
    inAppMessageInteractionHandlerFactory,
    SystemClock.instance
  )

  const evaluator = HackleCoreContext.get<DelegatingEvaluator>("delegatingEvaluator")

  const inAppMessageLayoutEvaluator = new InAppMessageLayoutEvaluator(
    new InAppMessageExperimentEvaluator(evaluator),
    new InAppMessageLayoutSelector(),
    eventRecorder
  )

  const inAppMessageRecorder = new InAppMessageRecorder(inAppMessageImpressionStorage)
  const inAppMessagePresentProcessor = new InAppMessagePresentProcessor(inAppMessageUi, inAppMessageRecorder)
  const inAppMessageIdentifierChecker = new InAppMessageIdentifierChecker()
  const inAppMessageEligibilityFlowFactory = new InAppMessageEligibilityFlowFactory(
    inAppMessageHiddenStorage,
    inAppMessageImpressionStorage,
    inAppMessageLayoutEvaluator
  )
  const inAppMessageLayoutResolver = new InAppMessageLayoutResolver(core, inAppMessageLayoutEvaluator)

  const inAppMessageDelayScheduler = new InAppMessageDelayScheduler(
    SystemClock.instance,
    new TimerScheduler("setTimeout")
  )

  const inAppMessageDelayStorage = new InAppMessageDelayLocalStorage(
    KeyTransformStorage.prefix(localStorage, `${CAMPAIGN_DELAY_STORAGE_KEY}_${sdkKey.slice(0, 8)}_`)
  )

  const inAppMessageDelayManager = new InAppMessageDelayManager(
    inAppMessageDelayScheduler,
    new Map(),
    inAppMessageDelayStorage
  )

  const inAppMessageEvaluateProcessor = new InAppMessageEvaluateProcessor(
    core,
    inAppMessageEligibilityFlowFactory,
    eventRecorder
  )

  const inAppMessageDeliverProcessor = new InAppMessageDeliverProcessor(
    workspaceManager,
    userManager,
    inAppMessageIdentifierChecker,
    inAppMessageLayoutResolver,
    inAppMessageEvaluateProcessor,
    inAppMessagePresentProcessor,
    sessionUserDecorator
  )
  const schedulers = [
    new DelayedInAppMessageScheduler(inAppMessageDeliverProcessor, inAppMessageDelayManager),
    new TriggeredInAppMessageScheduler(inAppMessageDeliverProcessor, inAppMessageDelayManager),
    new StoredInAppMessageScheduler(inAppMessageDeliverProcessor, inAppMessageDelayManager)
  ]
  const inAppMessageScheduleProcessor = new InAppMessageScheduleProcessor(
    new InAppMessageScheduleActionDeterminer(),
    new InAppMessageSchedulerFactory(schedulers)
  )

  inAppMessageDelayScheduler.listener = inAppMessageScheduleProcessor

  const inAppMessageEventMatcher = new InAppMessageEventMatcher(
    new InAppMessageEventTriggerRuleMatcher(HackleCoreContext.get<TargetMatcher>("targetMatcher"))
  )

  const inAppMessageTriggerDeterminer = new InAppMessageTriggerDeterminer(
    workspaceManager,
    inAppMessageEventMatcher,
    inAppMessageEvaluateProcessor
  )

  const inAppMessageTriggerHandler = new InAppMessageTriggerHandler(inAppMessageScheduleProcessor)
  const inAppMessageTriggerProcessor = new InAppMessageTriggerProcessor(
    inAppMessageTriggerDeterminer,
    inAppMessageTriggerHandler
  )
  const inAppMessageResetProcessor = new InAppMessageResetProcessor(
    inAppMessageIdentifierChecker,
    inAppMessageDelayManager
  )
  const inAppMessageManager = new InAppMessageManager(
    inAppMessageScheduleProcessor,
    inAppMessageDelayStorage,
    inAppMessageTriggerProcessor,
    inAppMessageResetProcessor,
    defaultEventProcessor,
    SystemClock.instance
  )

  userManager.addListener(inAppMessageManager)

  // GA Integration
  const googleAnalyticsDeterminer = new GoogleAnalyticsDeterminer(workspaceManager)
  const googleAnalyticsManager = new GoogleAnalyticsManager(defaultEventProcessor, googleAnalyticsDeterminer)

  // EngagementEventTracker
  const engagementEventTracker = new EngagementEventTracker(core, userManager)
  lifecycleCompositeManager.addEngagementListener(engagementEventTracker)

  // PageEventTracker
  const pageEventTracker = new PageEventTracker(core, userManager)

  if (config.automaticPageTracking) {
    lifecycleCompositeManager.addPageListener(pageEventTracker)
  }

  if (config.automaticRouteTracking) {
    lifecycleCompositeManager.addLifecycleListener(lifecycleCompositeManager.pageManager)
  }

  // SplitUrl
  const splitUrlMatcher = new SplitUrlMatcher(HackleCoreContext.get<TargetMatcher>("targetMatcher"))
  const splitUrlRedirectedCheckStorage = new SplitUrlRedirectedCookieStorage(
    cookieStorage,
    SPLIT_URL_REDIRECT_CHECK_COOKIE_KEY,
    { days: SPLIT_URL_REDIRECT_CHECK_COOKIE_EXPIRE_DAYS }
  )
  const splitUrlDeterminer = new SplitUrlDeterminer(
    workspaceManager,
    splitUrlMatcher,
    splitUrlRedirectedCheckStorage,
    core
  )
  const splitUrlRedirector = new SplitUrlRedirector(
    new SplitUrlResolver(campaignParser),
    new UrlReplaceHandler(),
    splitUrlRedirectedCheckStorage,
    initializeEventDispatcher
  )
  const splitUrlManager = new SplitUrlManager(splitUrlDeterminer, splitUrlRedirector, userManager, core)

  lifecycleCompositeManager.addPageListener(splitUrlManager)

  // UserExplorer
  const userExplorer = new HackleUserExplorerImpl(core, userManager, abOverrideStorage, ffOverrideStorage)
  const isValidDevToolConfig = config.devTool?.userExplorer && typeof config.devTool?.userExplorer === "function"
  const devTools = config.devTool && isValidDevToolConfig ? { manager: config.devTool, userExplorer } : undefined

  // Metrics
  if (config.enableMonitoring) {
    const monitoringMetricRegistry = new MonitoringMetricRegistry(
      Urls.monitoring(config.monitoringUrl),
      defaultTransport,
      synchronousTransport,
      new TimerScheduler("setTimeout"),
      METRIC_FLUSH_INTERVAL
    )

    Metrics.addRegistry(monitoringMetricRegistry)
    lifecycleCompositeManager.addLifecycleListener(monitoringMetricRegistry)
  }

  // add LifecycleManager Listener
  lifecycleCompositeManager.addLifecycleListener(sessionManager)
  lifecycleCompositeManager.addLifecycleListener(identifierManager)
  lifecycleCompositeManager.addLifecycleListener(retryManager)
  lifecycleCompositeManager.addLifecycleListener(browserEventProcessor)

  // Instantiate
  pollingSynchronizer.start()
  const fetchThrottler = Throttler.create(FETCH_THROTTLE_INTERVAL_MILLIS, 1)
  const hackleClient = new HackleClientImpl(
    core,
    pollingSynchronizer,
    sessionManager,
    userManager,
    piiEventManager,
    fetchThrottler,
    lifecycleCompositeManager.pageManager,
    inAppMessageUi,
    optOutManager,
    devTools
  )
  hackleClientHolder.set(hackleClient)

  // Post instantiate
  const autoOpenDevTool = () => {
    if (config.autoOpenDevTool) {
      if (!config.devTool) {
        log.error("DevTool is not provided")
      } else {
        hackleClient.showUserExplorer()
      }
    }
  }

  const refresh = () => {
    const timestamp = Date.now()
    const currentUser = userManager.currentUser

    // Session Refresh
    const currentSessionId = sessionManager.currentSessionId
    const newSessionId = previousUser
      ? sessionManager.startNewSessionIfNeeded({ oldUser: previousUser, newUser: currentUser, timestamp })
      : sessionManager.startNewSession(null, currentUser, timestamp)

    // Campaign Refresh
    if (newSessionId === currentSessionId) {
      // If new session is started, CampaignManager automatically restarts the campaign.
      // Attempts to cover when session is not newly started, but the campaign changes.
      campaignManager.startNewCampaignIfNeeded(currentUser, timestamp)
    }
  }

  new GlobalErrorHandler().install(core, userManager)

  refresh()

  hackleClient.onReady(() => {
    autoOpenDevTool()
    lifecycleCompositeManager.initialize()
    initializeEventDispatcher.dispatchDoneEventIfNeeded()
    inAppMessageManager.loadDelaySchedule()
  })

  return hackleClient
}

export { setAppWebViewInAppMessageBridge }
export { DefaultBrowserPropertyProvider }
export { PropertyOperationsBuilder, PropertyOperations } from "./hackle/property/PropertyOperations"
export { HackleUserExplorerBase }
export { createInstance }
export { getUserId }
export { setUserId }
export { removeUserId }
export { HackleClient }
export { PageView }
export { Logger }
export { Config }
export { EmptyParameterConfig, ParameterConfig } from "./core/internal/config/ParameterConfig"
export {
  HackleSubscriptionOperationsBuilder,
  HackleSubscriptionOperations
} from "./hackle/subscription/HackleSubscriptionOperations"
export { HackleSubscriptionStatus } from "./hackle/subscription/HackleSubscriptionStatus"

export * from "./core/internal/model/model"
export * from "./public/model"
export * from "./external-web/model"

export default {
  createInstance,
  getUserId,
  setUserId,
  removeUserId,
  Logger
}
